/**
 * Versement au coffre des transferts Credential Exchange (CXP) recus par l'app Android.
 *
 * L'OS remet a Klavox une charge CXF v1.0 en clair ; l'app Android la scelle aussitot
 * (AES-256-GCM, cle du Keystore) dans `files/cxp-inbox/` et expose ce pont a la WebView
 * qui execute ce coffre (`apps/android` CxpBridge). Une fois le coffre ouvert, chaque
 * charge est lue ici, passe par `parseCxfHeader`, est rechiffree sous la vaultKey par
 * `importEntries`, et seulement ALORS acquittee (le fichier scelle est supprime) : un
 * echec laisse la charge en attente, jamais perdue.
 *
 * Rien de la charge ne sort d'ici : ni journal, ni message d'erreur -- on ne compte que.
 */
import type { ImportedEntry } from './csv-import.js';
import { parseCxfHeader, type CxfHeader } from './cxf.js';

/** Pont `KlavoxAndroid` injecte par la WebView Android. */
export interface CxpInboxBridge {
  /** Identifiants des charges en attente, en JSON (`string[]`). */
  pending(): string;
  /** Charge CXF en clair (JSON), dechiffree par le Keystore. */
  open(id: string): string;
  /** Supprime la charge scellee : a n'appeler qu'apres l'enregistrement dans le coffre. */
  ack(id: string): void;
  /** Role source : un autre gestionnaire demande le coffre (l'utilisateur y a consenti cote Android). */
  exportRequested?(): boolean;
  /** Remet la charge CXF a l'OS (canal HPKE brokere) ; un seul appel par demande. */
  exportTo?(cxfJson: string): void;
}

export interface CxpDrainResult {
  imported: number;
  failed: number;
}

export async function drainCxpInbox(
  bridge: CxpInboxBridge,
  importEntries: (entries: readonly ImportedEntry[]) => Promise<unknown>,
): Promise<CxpDrainResult> {
  let imported = 0;
  let failed = 0;
  let ids: unknown;
  try {
    ids = JSON.parse(bridge.pending());
  } catch {
    return { imported, failed: 1 };
  }
  if (!Array.isArray(ids)) return { imported, failed: 1 };
  for (const id of ids) {
    if (typeof id !== 'string') continue;
    try {
      const entries = parseCxfHeader(JSON.parse(bridge.open(id)));
      await importEntries(entries);
      bridge.ack(id);
      imported += entries.length;
    } catch {
      // Le message pourrait citer la charge : on ne le garde pas, la charge reste en attente.
      failed += 1;
    }
  }
  return { imported, failed };
}

/**
 * Role source CXP : le coffre vient d'etre ouvert par le mot de passe maitre (c'est l'authentification dans
 * l'app source) ; s'il y a une demande en cours, la charge CXF est construite en memoire et remise au pont,
 * qui l'ecrit dans l'URI de l'OS. Elle ne passe par aucun stockage, journal ni message ici.
 */
export async function answerCxpExport(bridge: CxpInboxBridge, buildHeader: () => Promise<CxfHeader>): Promise<boolean> {
  if (bridge.exportRequested?.() !== true || bridge.exportTo === undefined) return false;
  bridge.exportTo(JSON.stringify(await buildHeader()));
  return true;
}
